import React, { useState} from "react";

import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { InputWithIcon } from "reusableComponents";
import { validateEditUserDataFields } from "helpers/validation";
import { emailIcon, passwordIcon, passwordConfirmIcon } from "assets/icons";
import { FormButton } from "reusableComponents";
import { updateUserData } from "services/dashboard.services"; // Import existing service
import { useLanguage, useModal } from "context";
import { getCurrentUser } from "store/auth/thunks";

import styles from "./styles.module.scss";

const PasswordEmailEditingPage = () => {
  const dispatch = useDispatch();
  const { openModal } = useModal(); // Assuming modal context is available
  const token = useSelector((state) => state.auth.token); // Assuming user data is stored in Redux
  const currentUser = useSelector((state) => state.auth.currentUser); // Assuming user data is stored in Redux

  const [t] = useTranslation("global");
  const { language } = useLanguage();

  // State management with backend-compatible keys
  const [inputValues, setInputValues] = useState({
    new_email: "",
    new_email_confirmation: "",
    newSecondEmail: "",
    secondEmailConfirmation: "",
    currentPassword: "",
    new_password: "",
    new_password_confirmation: "",
  });

  const [errors, setErrors] = useState([]);
  const [touchedFields, setTouchedFields] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setInputValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));

    if (touchedFields[name]) {
      realTimeValidation(e);
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouchedFields((prevTouched) => ({
      ...prevTouched,
      [name]: true,
    }));
    realTimeValidation(e);
  };

  const realTimeValidation = (e) => {
    const { name, value } = e.target;
    const errorMessage = validateEditUserDataFields(
      name,
      value,
      t,
      inputValues
    );

    setErrors(() => {
      if (errorMessage) {
        return [{ code: name, message: errorMessage }];
      } else {
        return [];
      }
    });
  };

  const handleSubmit = async (e, updateType) => {
    e.preventDefault();
    if (errors.length > 0) return;

    setIsLoading(true);
    try {
      const payload = { ...currentUser };
      if (updateType === "primaryEmail") {
        // payload.email = currentUser.email;
        payload.new_email = inputValues.new_email;
        payload.new_email_confirmation = inputValues.new_email_confirmation;
      } else if (updateType === "secondaryEmail") {
        payload.newSecondEmail = inputValues.newSecondEmail;
        payload.secondEmailConfirmation = inputValues.secondEmailConfirmation;
      } else if (updateType === "password") {
        payload.currentPassword = inputValues.currentPassword;
        payload.new_password = inputValues.new_password;
        payload.new_password_confirmation =
          inputValues.new_password_confirmation;
      }

      const response = await updateUserData(payload, token, language); // Call existing service function
      setInputValues({
        new_email: "",
        new_email_confirmation: "",
        newSecondEmail: "",
        secondEmailConfirmation: "",
        currentPassword: "",
        new_password: "",
        new_password_confirmation: "",
      });
      openModal({
        content: <p>{response.message}</p>,
        title: null, // Explicitly passing null for the title
        type: "success",
        autoClose: true,
        closeDelay: 1200,
      });
             if (token) {
            dispatch(getCurrentUser({language: language})); // Dispatch getCurrentUser
          }
    } catch (error) {
      if (error.errors) {
        setErrors(error.errors);
      } else {
        console.error("Unexpected error:", error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderInput = (name, placeholder, type = "text", icon) => {
    const errorObject = errors.find((error) => error.code === name);
    return (
      <InputWithIcon
        icon={icon}
        name={name}
        type={type}
        key={placeholder}
        handleBlur={handleBlur}
        handleChange={handleChange}
        placeholder={placeholder}
        value={inputValues[name] || ""}
        error={errorObject ? errorObject.message : ""}
        className={errorObject ? styles.inputError : ""}
      />
    );
  };

  return (
    <div
    className={`${styles.editPasswordPageContainer} ${
      isLoading ? styles.loading : ""
    }`}
  >        <h1 className={styles.title}>{t("dashboard.accessAndPassword")}</h1>

      {/* Primary Email Form */}
      <form
        className={styles.form}
        onSubmit={(e) => handleSubmit(e, "primaryEmail")}
      >
        <div className={styles.inputGroup}>
          <h2>{t("dashboard.contact.access")}</h2>
          {currentUser?.email && (
            <div className={styles.currentEmailTextHolder}>
              <span className={styles.label}>
                {t("dashboard.contact.currentEmail")}:{" "}
              </span>
              <span className={styles.email}>{currentUser.email}</span>
            </div>
          )}
          {renderInput(
            "new_email",
            "dashboard.contact.email",
            "email",
            emailIcon
          )}
          {renderInput(
            "new_email_confirmation",
            "dashboard.contact.emailConfirmation",
            "email",
            passwordConfirmIcon
          )}
          <div className={styles.submitButtonWrapper}>
            <FormButton
              label={t("buttons.save")}
              type="submit"
              variant = "gray"
              className={styles.submitButton}
              disabled={isLoading}
            />
          </div>
        </div>
      </form>

      {/* Secondary Email Form */}
      <form
        className={styles.form}
        onSubmit={(e) => handleSubmit(e, "secondaryEmail")}
      >
        <div className={styles.inputGroup}>
          <h2>{t("dashboard.contact.secondEmail")}</h2>
          {currentUser?.second_email && (
            <div className={styles.currentEmailTextHolder}>
              <span className={styles.label}>
                {t("dashboard.contact.secondEmail")}:{" "}
              </span>
              <span className={styles.email}>{currentUser.second_email}</span>
            </div>
          )}
          {renderInput(
            "newSecondEmail",
            "dashboard.contact.email",
            "email",
            emailIcon
          )}
          {renderInput(
            "secondEmailConfirmation",
            "dashboard.contact.emailConfirmation",
            "email",
            passwordConfirmIcon
          )}
          <div className={styles.submitButtonWrapper}>
            <FormButton
              label={t("buttons.save")}
              type="submit"
              variant="gray"
              className={styles.submitButton}
              disabled={isLoading}
            />
          </div>
        </div>
      </form>

      {/* Password Form */}
      <form
        className={styles.form}
        onSubmit={(e) => handleSubmit(e, "password")}
      >
        <div className={styles.inputGroup}>
          <h2>{t("modal.login.password")}</h2>
          {renderInput(
            "currentPassword",
            "dashboard.contact.currentPassword",
            "password",
            passwordIcon
          )}
          {renderInput(
            "new_password",
            "dashboard.contact.password",
            "password",
            passwordIcon
          )}
          {renderInput(
            "new_password_confirmation",
            "dashboard.contact.passwordConfirmation",
            "password",
            passwordConfirmIcon
          )}
          <div className={styles.submitButtonWrapper}>
            <FormButton
              label={t("buttons.save")}
              type="submit"
              variant="gray"
              className={styles.submitButton}
              disabled={isLoading}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default PasswordEmailEditingPage;
